import { type FC, useEffect, useState, useCallback, type ReactNode, Fragment } from 'react';
import { graphql, useRefetchableFragment, fetchQuery, useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';

import {
    trackEcommerce,
    type EcomProduct,
    trackingConstants,
    eventNameConstants,
} from 'dibs-tracking';
import { PageLinks } from 'dibs-page-links/exports/PageLinks.tsx';
import { useIntl } from 'dibs-react-intl';
import { getLang } from 'dibs-intl/exports/languages';
import { ModalBody } from 'dibs-elements/exports/ModalBody.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { TabProvider } from 'dibs-elements/exports/TabProvider.ts';
import { TabPanels } from 'dibs-elements/exports/TabPanels.tsx';
import { type TranslationState } from 'dibs-elements/exports/TranslationToggle.tsx';

import {
    SellerReviewsModalHeader,
    REVIEWS_TAB,
    PHOTOS_TAB,
} from './SellerReviewsModalHeader/SellerReviewsModalHeader.tsx';
import { Review } from './Review/Review.tsx';
import { ReviewPagination } from './ReviewPagination/ReviewPagination.tsx';
import { SellerReviewsPhotosCarouselRenderer } from './SellerReviewsPhotosCarouselRenderer.tsx';

import {
    Query,
    initialVars,
    getSortOrders,
    DEFAULT_SORT,
    type OrderBy,
} from '../SellerReviewsModalQuery.ts';
import { type SellerReviewsModalContentRefetchQuery } from './__generated__/SellerReviewsModalContentRefetchQuery.graphql.ts';
import { type SellerReviewsModalContent_viewer$key } from './__generated__/SellerReviewsModalContent_viewer.graphql.ts';
import {
    type SellerReviewsModalQuery,
    type SellerReviewsModalQuery$variables,
} from '../__generated__/SellerReviewsModalQuery.graphql.ts';

import styles from './SellerReviewsModalContent.scss';

const viewerFragment = graphql`
    fragment SellerReviewsModalContent_viewer on Viewer
    @refetchable(queryName: "SellerReviewsModalContentRefetchQuery") {
        feedbackFormPhotoUpload: featureFlag(feature: "feedbackFormPhotoUpload")
        orderFeedbackMultiple(
            sellerId: $sellerId
            excludeInAppealProcess: true
            orderByNewest: $orderByNewest
            orderByHighest: $orderByHighest
            orderByLowest: $orderByLowest
            excludePrivateItemReviews: true
            first: 3
            page: $page
        ) {
            metadata {
                aggregatedFeedback {
                    overallExperience {
                        mean
                    }
                }
            }
            numberOfPages
            edges {
                node {
                    serviceId
                    item {
                        serviceId
                        title
                        contemporaryTrackingString
                    }
                    publicNote {
                        ... on StandardQuestion {
                            language
                        }
                    }
                    ...Review_review
                }
            }
            ...SellerReviewsModalHeader_reviews
        }
        ...SellerReviewsModalHeader_viewer
    }
`;

export const SellerReviewsModalContent: FC<{
    viewer: SellerReviewsModalContent_viewer$key | null | undefined;
    isLoading?: boolean;
    sellerId: string;
    contentOnly?: boolean;
    noTabs?: boolean;
}> = ({ sellerId, isLoading, contentOnly, noTabs, ...props }) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const [viewer, fragmentRefetch] = useRefetchableFragment<
        SellerReviewsModalContentRefetchQuery,
        SellerReviewsModalContent_viewer$key
    >(viewerFragment, props.viewer);

    const [isRefetching, setIsRefetching] = useState(false);
    const refetch = useCallback(
        (variables: SellerReviewsModalQuery$variables): void => {
            setIsRefetching(true);

            //workaround https://relay.dev/docs/next/guided-tour/refetching/refetching-fragments-with-different-data/#if-you-need-to-avoid-suspense
            fetchQuery<SellerReviewsModalQuery>(environment, Query, variables).subscribe({
                complete: () => {
                    setIsRefetching(false);
                    fragmentRefetch(variables);
                },
                error: () => {
                    setIsRefetching(false);
                },
            });
        },
        [environment, setIsRefetching, fragmentRefetch]
    );

    const [currentPage, setCurrentPage] = useState(initialVars.page);
    const [sortOrder, setSortOrder] = useState<OrderBy>(DEFAULT_SORT);
    const [activeTab, setActiveTab] = useState(REVIEWS_TAB);
    const [translationState, setTranslationState] = useState<TranslationState>('HIDDEN');

    const feedbackFormPhotoUpload = !!viewer?.feedbackFormPhotoUpload;
    const reviews = viewer?.orderFeedbackMultiple || null;
    const numberOfPages = reviews?.numberOfPages;
    const feedbackMean = reviews?.metadata?.aggregatedFeedback?.overallExperience?.mean;

    useEffect(() => {
        if (translationState === 'HIDDEN') {
            reviews?.edges?.forEach(review => {
                const { publicNote } = review?.node || {};
                if (publicNote?.language && publicNote.language !== getLang(intl.locale)) {
                    setTranslationState('SHOW_ORIGINAL');
                }
            });
        }
    }, [intl.locale, reviews, translationState]);

    useEffect(() => {
        const { ECOM_PRODUCT_IMPRESSION } = trackingConstants;
        const { EVENT_VIEW_ITEM_LIST } = eventNameConstants;
        if (!isRefetching && !isLoading && reviews?.edges) {
            trackEcommerce({
                type: ECOM_PRODUCT_IMPRESSION,
                eventName: EVENT_VIEW_ITEM_LIST,
                actionField: {
                    list: 'sellerReviews',
                },
                products: reviews.edges.reduce<EcomProduct[]>((acc, review, index) => {
                    const item = review?.node?.item;
                    if (item) {
                        acc.push({
                            id: item.serviceId,
                            name: item.title,
                            brand: sellerId,
                            dimension83: item.contemporaryTrackingString,
                            position: index,
                        });
                    }
                    return acc;
                }, []),
            });
        }
    }, [reviews, sellerId, isRefetching, isLoading]);

    const onChangePage = useCallback(
        (pageNumber: number): void => {
            setCurrentPage(pageNumber);

            refetch({ ...initialVars, page: pageNumber, sellerId, ...getSortOrders(sortOrder) });
        },
        [setCurrentPage, refetch, sortOrder, sellerId]
    );

    const onSortOrderChange = useCallback(
        (newSortOrder: OrderBy): void => {
            setSortOrder(newSortOrder);
            setCurrentPage(1);

            refetch({ ...initialVars, sellerId, ...getSortOrders(newSortOrder) });
        },
        [setSortOrder, refetch, sellerId]
    );

    const content = (
        <TabPanels>
            {[
                REVIEWS_TAB,
                <Fragment key={REVIEWS_TAB}>
                    {isLoading || isRefetching ? (
                        <div
                            className={classnames(styles.spinnerWrapper, {
                                [styles.noTabsWrapper]: noTabs,
                            })}
                        >
                            <Spinner dark />
                        </div>
                    ) : (
                        reviews?.edges?.reduce<ReactNode[]>((acc, review, index) => {
                            if (review?.node?.serviceId) {
                                acc.push(
                                    <Review
                                        key={`seller-review-${review.node.serviceId}`}
                                        translationState={noTabs ? undefined : translationState}
                                        review={review.node}
                                        showOrderPhoto={feedbackFormPhotoUpload}
                                        noTabs={noTabs}
                                        isLastRow={index === (reviews?.edges?.length || 0) - 1}
                                    />
                                );
                            }
                            return acc;
                        }, [])
                    )}
                    {!!numberOfPages &&
                        (noTabs ? (
                            <PageLinks
                                currentPage={currentPage}
                                numberOfPages={numberOfPages || 0}
                                onPageChange={({ pageNumber }: { pageNumber: number }) =>
                                    onChangePage(pageNumber)
                                }
                                totalPageLinks={7}
                                noUnderline
                                align="left"
                            />
                        ) : (
                            <ReviewPagination
                                currentPage={currentPage}
                                numberOfPages={numberOfPages || 0}
                                onChangePage={onChangePage}
                            />
                        ))}
                </Fragment>,
            ]}
            {feedbackFormPhotoUpload && [
                PHOTOS_TAB,
                <SellerReviewsPhotosCarouselRenderer key={PHOTOS_TAB} sellerId={sellerId} />,
            ]}
        </TabPanels>
    );

    return (
        <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
            <SellerReviewsModalHeader
                isCompact={contentOnly}
                avgOverallRating={feedbackMean}
                translationState={translationState}
                onTranslateChange={setTranslationState}
                onSortOrderChange={onSortOrderChange}
                activeTab={activeTab}
                noTabs={noTabs}
                viewer={viewer}
                reviews={reviews}
            />
            {contentOnly ? (
                <div className={classnames(styles.reviewsContainer, { [styles.noTabs]: noTabs })}>
                    {content}
                </div>
            ) : (
                <ModalBody className={styles.reviewsContainer}>{content}</ModalBody>
            )}
        </TabProvider>
    );
};
